import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import OrganizationBrand from '@/components/OrganizationBrand';
import SectionLabel from '@/components/navigation/SectionLabel';
import ModuleSwitcher from '@/components/ModuleSwitcher';
import InboxLink from '@/components/InboxLink';
import { NAV_SECTIONS, getDevelopmentSection, getVisibleSections } from '@/lib/navigationSections';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasCapability, hasModule, sessionOffline } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [developmentMode, setDevelopmentMode] = useState(() => localStorage.getItem('zoea-development-mode') === 'on');

  const developmentAccess = ['flow_view', 'flow_manage', 'flow_activate', 'flow_audit_view', 'usecases_view', 'usecases_manage'].some(hasCapability);
  const sections = getVisibleSections(NAV_SECTIONS, { hasCapability, hasModule });
  const developmentSection = getDevelopmentSection({ hasCapability, developmentMode: developmentMode && developmentAccess });

  const isActive = (href) => location.pathname === href;

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const isDemo = Boolean(user?.is_demo);
  const toggleDevelopmentMode = () => {
    const next = !developmentMode;
    setDevelopmentMode(next);
    if (next) localStorage.setItem('zoea-development-mode', 'on');
    else localStorage.removeItem('zoea-development-mode');
  };

  return (
    <div className="min-h-screen max-w-full overflow-x-hidden bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        print:hidden
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
            <Link to="/dashboard" className="flex items-center gap-3">
              <OrganizationBrand />
            </Link>
            <button 
              className="lg:hidden text-slate-400 hover:text-slate-600"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {[...sections, ...(developmentSection ? [developmentSection] : [])].map((section, index) => (
              <div key={section.id} className={index > 0 ? 'mt-4 border-t border-slate-100 pt-4' : ''}>
                {section.label && <SectionLabel name={section.label} compact />}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`sidebar-item ${isActive(item.href) ? 'active' : ''}`}
                      aria-current={isActive(item.href) ? 'page' : undefined}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.id}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                  {section.id === 'admin' && user?.is_platform_admin && (
                    <Link
                      to="/superadmin"
                      className={`sidebar-item ${isActive('/superadmin') ? 'active' : ''}`}
                      aria-current={isActive('/superadmin') ? 'page' : undefined}
                      onClick={() => setSidebarOpen(false)}
                      data-testid="nav-platform-admin"
                    >
                      <ShieldCheck className="w-5 h-5" />
                      <span className="font-medium">Platform-beheer</span>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </nav>

          {/* User */}
          <div className="px-4 py-4 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-medium">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Instellingen
                </DropdownMenuItem>
                {developmentAccess && <DropdownMenuItem onClick={toggleDevelopmentMode} data-testid="development-mode-toggle">
                  <span className="mr-2">🧩</span>
                  {developmentMode ? 'Ontwikkelmodus uitzetten' : 'Ontwikkelmodus'}
                </DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="min-w-0 lg:pl-64 print:pl-0">
        {sessionOffline && (
          <div className="print:hidden sticky top-0 z-40 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-900">
            Je bent offline. Je sessie blijft bewaard; actuele gegevens worden geladen zodra de verbinding terug is.
          </div>
        )}
        {isDemo && (
          <div className="print:hidden sticky top-0 z-40 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-amber-100 px-4 py-2 text-center text-xs font-medium text-amber-950 border-b border-amber-200">
            <span>Je bekijkt een tijdelijke Zoea-demo met fictieve gegevens.</span>
            <button onClick={handleLogout} className="font-bold underline underline-offset-2 hover:text-amber-700">Demo verlaten</button>
          </div>
        )}
        {/* Top bar */}
        <header className={`print:hidden ${isDemo ? 'sticky top-[33px]' : 'sticky top-0'} z-30 bg-white/80 backdrop-blur-lg border-b border-slate-100`}>
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <button 
              className="lg:hidden text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex-1" />
            {developmentMode && developmentAccess && <span className="hidden items-center gap-1 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 sm:flex"><span aria-hidden="true">🧩</span> Ontwikkelmodus</span>}
            <ModuleSwitcher />
            
            <div className="flex items-center gap-3">
              <InboxLink />
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="min-w-0 max-w-full overflow-x-hidden p-4 sm:p-6 lg:p-8 print:p-0">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
